/**
 * ComingUpList (LP-24, was LP-11's Today band): the head of the My Clients
 * page's left column, a compact agenda. Today, tomorrow and the day after,
 * each a row: the day's name on the left, its lines on the right. The
 * lines are the appointments across the viewer's clients, the care visits
 * planned for them (a care manager's own reads "Visit", another's names
 * her), and the viewer's SPICE sessions, in time order. A care visit today
 * carries the Note action (a later one has nothing to note yet). A day
 * with nothing says so in one line; with nothing in all three days the
 * section is one quiet line with no card. Each day shows five lines, then
 * "Show all N".
 *
 * The lines come from the home read's `coming_up`. An older cached read
 * without it falls back to today's lines from the fields it does carry.
 */
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/primitives/Button';
import type { CmHomeSnapshot } from '@/lib/cmHomeApi';
import { MY_CLIENTS_HEADINGS } from '@/lib/headingCase';
import { parseIsoDate, proseUpcomingDay } from '@/lib/proseDate';
import { QuietSection, ROW_CAP, ROW_LIST, SECTION_CARD, ShowAllToggle } from '@/components/home/RailParts';

interface Line {
  key: string;
  date: string;
  time: string | null;
  clientId: string;
  name: string;
  what: string;
  /** A care visit's line: the Note action opens the client's notes. */
  noteAction: boolean;
}

type ComingUpHome = Pick<CmHomeSnapshot, 'clients' | 'my_week' | 'care_visits_today' | 'coming_up'>;

/** How many days Coming Up covers, today included (mirrors
 *  api/_shared/comingUpShape.ts COMING_UP_DAYS). */
export const COMING_UP_DAYS = 3;

function plusDaysIso(iso: string, n: number): string {
  const d = parseIsoDate(iso);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function comingUpDays(todayIso: string): string[] {
  return Array.from({ length: COMING_UP_DAYS }, (_, i) => plusDaysIso(todayIso, i));
}

/** "Today", "Tomorrow", then the weekday. */
export function dayHead(dateIso: string, todayIso: string): string {
  if (dateIso === todayIso) return 'Today';
  if (dateIso === plusDaysIso(todayIso, 1)) return 'Tomorrow';
  const w = proseUpcomingDay(dateIso, todayIso);
  return w.charAt(0).toUpperCase() + w.slice(1);
}

/** Today's lines from the fields an older read carries. */
function todayOnlyLines(home: ComingUpHome, todayIso: string): Line[] {
  const lines: Line[] = home.clients
    .filter((c) => c.visit_today)
    .map((c) => ({ key: `v-${c.id}`, date: todayIso, time: c.visit_today_time ?? null, clientId: c.id, name: c.display_name, what: 'Visit', noteAction: false }));
  for (const v of home.care_visits_today ?? []) {
    lines.push({
      key: `c-${v.id}`,
      date: todayIso,
      time: v.time,
      clientId: v.client_id,
      name: v.client_display_name,
      what: v.is_mine ? 'Visit' : `Visit with ${v.care_manager_name}`,
      noteAction: true,
    });
  }
  const day = home.my_week?.days.find((d) => d.date === todayIso);
  for (const v of day?.visits ?? []) {
    lines.push({ key: `s-${v.client_id}-${v.start_time}`, date: todayIso, time: v.start_time.slice(0, 5), clientId: v.client_id, name: v.client_display_name, what: 'SPICE session', noteAction: false });
  }
  // Timed lines in time order; untimed ones after them.
  return lines.sort((a, b) => (a.time ?? '99:99').localeCompare(b.time ?? '99:99') || a.name.localeCompare(b.name));
}

export function comingUpLines(home: ComingUpHome | null, todayIso: string): Line[] {
  if (home === null) return [];
  if (home.coming_up === undefined) return todayOnlyLines(home, todayIso);
  return home.coming_up.map((l) => ({
    key: l.key,
    date: l.date,
    time: l.time,
    clientId: l.client_id,
    name: l.client_display_name,
    what:
      l.kind === 'spice'
        ? 'SPICE session'
        : l.kind === 'care_visit' && !l.is_mine && l.care_manager_name
          ? `Visit with ${l.care_manager_name}`
          : 'Visit',
    noteAction: l.kind === 'care_visit',
  }));
}

export function ComingUpList({
  home,
  todayIso,
  tenantSlug,
  canWriteClinical = false,
}: {
  home: ComingUpHome | null;
  todayIso: string;
  tenantSlug: string;
  /** The viewer may write a case note: a care visit line offers Note. */
  canWriteClinical?: boolean;
}): ReactNode {
  const navigate = useNavigate();
  const [uncapped, setUncapped] = useState<ReadonlySet<string>>(() => new Set());
  const lines = comingUpLines(home, todayIso);
  const days = comingUpDays(todayIso);
  const toggle = (day: string): void =>
    setUncapped((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  if (lines.length === 0) {
    return (
      <section aria-labelledby="my-clients-today-heading" data-testid="my-clients-today">
        <QuietSection headingId="my-clients-today-heading" title={MY_CLIENTS_HEADINGS.comingUp}>
          <p data-testid="my-clients-today-empty" className="text-title-sm text-ink-soft">The next three days are clear.</p>
        </QuietSection>
      </section>
    );
  }
  return (
    <section aria-labelledby="my-clients-today-heading" data-testid="my-clients-today" className={SECTION_CARD}>
      <h2 id="my-clients-today-heading" className="mb-1 font-display text-lg leading-7 text-sage-800">
        {MY_CLIENTS_HEADINGS.comingUp}
      </h2>
      <div className={ROW_LIST}>
        {days.map((day) => {
          const dayLines = lines.filter((l) => l.date === day);
          const shown = uncapped.has(day) ? dayLines : dayLines.slice(0, ROW_CAP);
          return (
            <div key={day} data-testid="my-clients-day" data-date={day} className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-x-3">
              <h3 data-testid={`my-clients-day-head-${day}`} className="py-2 text-title-sm font-semibold leading-6 text-sage-800">
                {dayHead(day, todayIso)}
              </h3>
              <div className="flex min-w-0 flex-col">
                {dayLines.length === 0 ? (
                  <p data-testid="my-clients-day-empty" className="py-2 text-title-sm leading-6 text-ink-soft">Nothing planned.</p>
                ) : (
                  <ul className={`m-0 list-none p-0 ${ROW_LIST}`}>
                    {shown.map((l) => (
                      <li key={l.key} data-testid="my-clients-today-line" className="flex items-center gap-2 py-2 text-title-sm leading-6 text-ink-soft">
                        {/* The line wraps rather than cutting: a visit line can name
                            another care manager beside the Note action. An untimed
                            line starts with the client: the day says when. */}
                        <span className="min-w-0 flex-1 break-words">
                          {l.time !== null && (
                            <>
                              <span className="font-semibold tabular-nums text-ink">{l.time}</span>
                              {' · '}
                            </>
                          )}
                          <Link to={`/t/${tenantSlug}/clients/${l.clientId}`} className="font-semibold text-sage-800 hover:underline">
                            {l.name}
                          </Link>
                          {' · '}
                          {l.what}
                        </span>
                        {l.noteAction && l.date === todayIso && canWriteClinical && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="shrink-0"
                            data-testid="my-clients-today-note"
                            onClick={() => navigate(`/t/${tenantSlug}/clients/${l.clientId}?tab=notes`)}
                          >
                            + Note
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <ShowAllToggle total={dayLines.length} open={uncapped.has(day)} onToggle={() => toggle(day)} testId={`my-clients-day-show-all-${day}`} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
